import { Crown, Lock, Mic, MicOff, Settings, ShieldAlert, Video, VideoOff } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { formatMeetingId, LIMITS, type MeetingPublicInfo } from '../../shared/protocol';
import { Avatar, Button, LevelBar, useTrackStream, VideoView } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { describeMediaError, deviceLabel, getCameraTrack, getMicrophoneTrack, refreshDevices, supports, useDevices } from '../lib/media';
import { updateSettings, useSettings } from '../lib/settings';
import { SettingsDialog } from '../settings/SettingsDialog';

export interface PreJoinResult {
  name: string;
  passcode: string | null;
  micTrack: MediaStreamTrack | null;
  cameraTrack: MediaStreamTrack | null;
  audioMuted: boolean;
  videoOn: boolean;
}

interface Props {
  meeting: MeetingPublicInfo;
  isHost: boolean;
  initialName: string;
  initialAudioMuted: boolean;
  initialVideoOff: boolean;
  needsPasscode: boolean;
  passcodeError: string | null;
  autoJoin: boolean;
  onJoin(result: PreJoinResult): void;
  onCancel(): void;
}

/** Device check before entering: camera preview, mic level, name and passcode. */
export function PreJoin(props: Props) {
  const settings = useSettings();
  const devices = useDevices();
  const [name, setName] = useState(props.initialName);
  const [passcode, setPasscode] = useState('');
  const [audioMuted, setAudioMuted] = useState(props.initialAudioMuted);
  const [videoOn, setVideoOn] = useState(!props.initialVideoOff);
  const [mic, setMic] = useState<MediaStreamTrack | null>(null);
  const [camera, setCamera] = useState<MediaStreamTrack | null>(null);
  const [micError, setMicError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [loadingCamera, setLoadingCamera] = useState(!props.initialVideoOff);
  const [micPending, setMicPending] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const handedOver = useRef(false);
  const tracks = useRef<{ mic: MediaStreamTrack | null; camera: MediaStreamTrack | null }>({ mic: null, camera: null });
  const cameraStream = useTrackStream(camera);

  // Microphone: opened once (and reopened when the selected device changes).
  useEffect(() => {
    let cancelled = false;
    setMicError(null);
    setMicPending(true);
    getMicrophoneTrack(settings)
      .then((t) => {
        // A track that arrives after the user joined or left is released immediately.
        if (cancelled || handedOver.current) return t.stop();
        tracks.current.mic?.stop();
        tracks.current.mic = t;
        setMic(t);
      })
      .catch((err) => {
        if (cancelled) return;
        setMicError(describeMediaError(err, 'microphone'));
        setAudioMuted(true);
      })
      .finally(() => !cancelled && setMicPending(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.audioInputId]);

  // Camera: only open while the user wants video on.
  useEffect(() => {
    if (!videoOn) {
      setLoadingCamera(false);
      tracks.current.camera?.stop();
      tracks.current.camera = null;
      setCamera(null);
      return;
    }
    let cancelled = false;
    setLoadingCamera(true);
    setCameraError(null);
    getCameraTrack(settings)
      .then((t) => {
        if (cancelled || handedOver.current) return t.stop();
        tracks.current.camera?.stop();
        tracks.current.camera = t;
        setCamera(t);
      })
      .catch((err) => {
        if (cancelled) return;
        setCameraError(describeMediaError(err, 'camera'));
        setVideoOn(false);
      })
      .finally(() => !cancelled && setLoadingCamera(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoOn, settings.videoInputId, settings.hdVideo]);

  useEffect(() => {
    if (mic) mic.enabled = !audioMuted;
  }, [mic, audioMuted]);

  // Stop the preview tracks if the user leaves without joining.
  useEffect(() => {
    void refreshDevices();
    return () => {
      if (handedOver.current) return;
      tracks.current.mic?.stop();
      tracks.current.camera?.stop();
    };
  }, []);

  // Joining waits for the devices so the call never starts without the tracks the user chose.
  const devicesReady = !micPending && !loadingCamera;
  const canJoin = devicesReady && name.trim().length > 0 && (!props.needsPasscode || passcode.trim().length > 0);

  const join = (e?: FormEvent) => {
    e?.preventDefault();
    if (!canJoin) return;
    handedOver.current = true;
    updateSettings({ displayName: name.trim() });
    props.onJoin({
      name: name.trim(),
      passcode: props.needsPasscode ? passcode.trim() : null,
      micTrack: tracks.current.mic,
      cameraTrack: videoOn ? tracks.current.camera : null,
      audioMuted: audioMuted || !tracks.current.mic,
      videoOn: videoOn && !!tracks.current.camera,
    });
  };

  // "Show video preview first" disabled: join as soon as devices settle.
  const autoJoined = useRef(false);
  useEffect(() => {
    if (!props.autoJoin || autoJoined.current || !canJoin) return;
    const micSettled = mic || micError;
    const camSettled = !videoOn || camera || cameraError;
    if (micSettled && camSettled) {
      autoJoined.current = true;
      join();
    }
  });

  const selectedMic = settings.audioInputId || 'default';

  return (
    <div className="prejoin meeting-theme">
      <form className="prejoin-card" onSubmit={join}>
        <div className="prejoin-preview">
          {videoOn && cameraStream ? (
            <VideoView stream={cameraStream} mirror={settings.mirrorVideo} />
          ) : (
            <div className="prejoin-avatar">
              <Avatar name={name || '?'} size={96} />
              <span>{loadingCamera ? 'Abriendo la cámara…' : cameraError ? 'Cámara no disponible' : 'Tu cámara está apagada'}</span>
            </div>
          )}
          <div className="prejoin-name">{name || 'Tu nombre'}</div>
          <div className="prejoin-toggles">
            <button
              type="button"
              className={`round-btn ${audioMuted ? 'off' : ''}`}
              onClick={() => setAudioMuted((m) => !m)}
              disabled={!mic}
              aria-pressed={!audioMuted}
              aria-label={audioMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
              title={audioMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
            >
              {audioMuted ? <MicOff size={20} /> : <Mic size={20} />}
            </button>
            <button
              type="button"
              className={`round-btn ${videoOn ? '' : 'off'}`}
              onClick={() => setVideoOn((v) => !v)}
              aria-pressed={videoOn}
              aria-label={videoOn ? 'Detener vídeo' : 'Iniciar vídeo'}
              title={videoOn ? 'Detener vídeo' : 'Iniciar vídeo'}
            >
              {videoOn ? <Video size={20} /> : <VideoOff size={20} />}
            </button>
            <button type="button" className="round-btn" onClick={() => setShowSettings(true)} aria-label="Configuración de audio y vídeo" title="Configuración">
              <Settings size={20} />
            </button>
          </div>
        </div>

        <div className="prejoin-side">
          <div>
            <h1 className="prejoin-title">{props.meeting.title}</h1>
            <p className="muted">
              ID {formatMeetingId(props.meeting.id)}
              {props.meeting.startTime && ` · ${formatDateTime(props.meeting.startTime)}`}
            </p>
            <p className="prejoin-status">
              {props.isHost ? (
                <>
                  <Crown size={14} /> Entrarás como anfitrión
                </>
              ) : props.meeting.live ? (
                `${props.meeting.participantCount} ${props.meeting.participantCount === 1 ? 'persona' : 'personas'} en la reunión`
              ) : (
                'Todavía no ha entrado nadie'
              )}
            </p>
          </div>

          <label className="field">
            <span>Tu nombre</span>
            <input className="input" value={name} maxLength={LIMITS.nameLength} onChange={(e) => setName(e.target.value)} placeholder="Cómo te verán los demás" autoFocus={!name} />
          </label>

          {props.needsPasscode && (
            <label className="field">
              <span>
                <Lock size={13} style={{ verticalAlign: -2 }} /> Código de acceso
              </span>
              <input className="input" value={passcode} onChange={(e) => setPasscode(e.target.value)} aria-invalid={!!props.passcodeError} autoFocus={!!name} maxLength={LIMITS.passcodeLength} />
              {props.passcodeError && <span className="error-text">{props.passcodeError}</span>}
            </label>
          )}

          <div className="field">
            <span className="label">Micrófono</span>
            <select className="select" value={selectedMic} onChange={(e) => updateSettings({ audioInputId: e.target.value })} disabled={!devices.audioInputs.length}>
              {!devices.audioInputs.length && <option>Sin micrófono</option>}
              {devices.audioInputs.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>
                  {deviceLabel(d, i, 'Micrófono')}
                </option>
              ))}
            </select>
            {micError ? <span className="error-text">{micError}</span> : <LevelBar track={audioMuted ? null : mic} bars={16} />}
          </div>

          {supports.outputSelection && devices.audioOutputs.length > 0 && (
            <label className="field">
              <span>Altavoz</span>
              <select className="select" value={settings.audioOutputId} onChange={(e) => updateSettings({ audioOutputId: e.target.value })}>
                {devices.audioOutputs.map((d, i) => (
                  <option key={d.deviceId || i} value={d.deviceId}>
                    {deviceLabel(d, i, 'Altavoz')}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="field">
            <span>Cámara</span>
            <select
              className="select"
              value={settings.videoInputId || camera?.getSettings().deviceId || ''}
              onChange={(e) => updateSettings({ videoInputId: e.target.value })}
              disabled={!devices.videoInputs.length}
            >
              {!devices.videoInputs.length && <option value="">Sin cámara</option>}
              {devices.videoInputs.map((d, i) => (
                <option key={d.deviceId || i} value={d.deviceId}>
                  {deviceLabel(d, i, 'Cámara')}
                </option>
              ))}
            </select>
            {cameraError && <span className="error-text">{cameraError}</span>}
          </label>

          {(micError || cameraError) && (
            <div className="callout callout-warning">
              <ShieldAlert size={16} />
              <span>Puedes entrar igualmente; podrás activar los dispositivos más tarde desde la barra de la reunión.</span>
            </div>
          )}

          <div className="prejoin-actions">
            <Button variant="ghost" onClick={props.onCancel}>
              Cancelar
            </Button>
            <Button variant="primary" size="lg" type="submit" disabled={!canJoin}>
              {!devicesReady ? 'Preparando dispositivos…' : props.isHost && !props.meeting.live ? 'Iniciar reunión' : 'Unirse'}
            </Button>
          </div>
        </div>
      </form>
      {showSettings && <SettingsDialog initialTab="audio" onClose={() => setShowSettings(false)} />}
    </div>
  );
}
